import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { AutoComplete } from 'primereact/autocomplete';
import { toast } from 'react-toastify';

// Components
import CustomButton from '../CustomButton';

// APIs
import { mergeCompaniesApi, searchCompanyApi } from '../../redux/apis/company';

const MergeCompanyModal = ({ show, onHide, sourceCompanies = [], onMergeSuccess }) => {
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedTargetCompany, setSelectedTargetCompany] = useState(null);
  const [targetSearchQuery, setTargetSearchQuery] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [isSearchingTarget, setIsSearchingTarget] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [loading, setLoading] = useState(false);

  // Validate target company against selected sources
  const validateTarget = (queryStr, targetObj, sourcesList) => {
    const trimmed = (targetObj ? targetObj.name : queryStr || '').trim();
    if (!trimmed) {
      setValidationError('');
      return true;
    }

    const targetId = targetObj?.id || targetObj?.ID;
    const sources = sourcesList || selectedSources;

    // Remaining companies that will actually be merged into target
    const remaining = sources.filter((c) => {
      const cId = c.id || c.ID;
      const cName = (c.name || c.CompanyName || c.companyName || '').trim();
      if (targetId && cId && cId === targetId) return false;
      if (cName && cName === trimmed) return false;
      return true;
    });

    if (sources.length > 0 && remaining.length === 0) {
      setValidationError(
        'Cannot merge a company into itself. Please enter a different target company name.'
      );
      return false;
    }

    setValidationError('');
    return true;
  };

  // Reset state whenever modal opens or closes
  useEffect(() => {
    if (show) {
      setSelectedSources(sourceCompanies || []);
      setSelectedTargetCompany(null);
      setTargetSearchQuery('');
      setSearchSuggestions([]);
      setIsSearchingTarget(false);
      setValidationError('');
    }
  }, [show, sourceCompanies]);

  const handleRemoveSourceCompany = (companyIdOrName) => {
    const updated = selectedSources.filter((c) => (c.id || c.ID || c.name) !== companyIdOrName);
    setSelectedSources(updated);
    validateTarget(targetSearchQuery, selectedTargetCompany, updated);
  };

  // Autocomplete search handler - searches all companies (both master and regular)
  const handleSearchCompanies = async (event) => {
    try {
      const query = event.query || '';
      setTargetSearchQuery(query);
      if (!query || query.trim().length === 0) {
        setSearchSuggestions([]);
        return;
      }
      setIsSearchingTarget(true);
      const res = await searchCompanyApi(query.trim());
      const items = res?.data?.data || res?.data || [];
      setSearchSuggestions(items);
    } catch (err) {
      console.error('Error searching target companies:', err);
      setSearchSuggestions([]);
    } finally {
      setIsSearchingTarget(false);
    }
  };

  const handleInputChange = (e) => {
    const val = typeof e.value === 'string' ? e.value : e.value?.name || '';
    setTargetSearchQuery(val);
    if (typeof e.value === 'string') {
      setSelectedTargetCompany(null);
      validateTarget(val, null, selectedSources);
    } else {
      setSelectedTargetCompany(e.value);
      validateTarget(e.value?.name || '', e.value, selectedSources);
    }
  };

  const handleSelectTarget = (e) => {
    const selected = e.value;
    setSelectedTargetCompany(selected);
    const targetName = selected?.name || '';
    setTargetSearchQuery(targetName);
    validateTarget(targetName, selected, selectedSources);
  };

  // Submit merge request
  const handleConfirmMerge = async () => {
    if (selectedSources.length === 0) {
      toast.warning('Please select at least one company to merge.');
      return;
    }

    const finalTargetName = (
      selectedTargetCompany ? selectedTargetCompany.name : targetSearchQuery || ''
    ).trim();

    if (!finalTargetName) {
      setValidationError('Please select or enter a Target Company name.');
      return;
    }

    const isValid = validateTarget(targetSearchQuery, selectedTargetCompany, selectedSources);
    if (!isValid) {
      toast.warning(
        'Cannot merge a company into itself. Please enter a different target company name.'
      );
      return;
    }

    const targetCompanyId = selectedTargetCompany?.id || selectedTargetCompany?.ID || undefined;

    // Filter out target from sources to get the secondary companies to merge
    const remainingSources = selectedSources.filter((c) => {
      const cId = c.id || c.ID;
      const cName = (c.name || c.CompanyName || c.companyName || '').trim();
      if (targetCompanyId && cId && cId === targetCompanyId) return false;
      if (cName && cName === finalTargetName) return false;
      return true;
    });

    if (remainingSources.length === 0) {
      setValidationError('Cannot merge a company into itself.');
      toast.warning('Cannot merge a company into itself.');
      return;
    }

    const mergeCompanyIds = remainingSources.map((c) => c.id || c.ID).filter(Boolean);
    const mergeCompanyNames = remainingSources
      .map((c) => c.name || c.CompanyName || c.companyName)
      .filter(Boolean);

    const payload = {
      targetCompanyId,
      targetName: finalTargetName,
      mergeCompanyIds,
      mergeCompanyNames
    };

    try {
      setLoading(true);
      const res = await mergeCompaniesApi(payload);
      if (res?.status === 200 || res?.data?.success) {
        toast.success(`Companies successfully merged into "${finalTargetName}"!`);
        onMergeSuccess?.();
        onHide();
      } else {
        toast.success(`Companies successfully merged into "${finalTargetName}"!`);
        onMergeSuccess?.();
        onHide();
      }
    } catch (error) {
      console.error('Merge error:', error);
      toast.error(error?.response?.data?.message || 'Failed to merge companies.');
    } finally {
      setLoading(false);
    }
  };

  const isTargetEmpty = !(selectedTargetCompany?.name || targetSearchQuery?.trim());
  const isSubmitDisabled =
    loading || selectedSources.length === 0 || isTargetEmpty || Boolean(validationError);

  const footerContent = (
    <div className="rpt-modal-footer">
      <CustomButton variant="outlined" onClick={onHide} disabled={loading}>
        Cancel
      </CustomButton>
      <CustomButton variant="contained" onClick={handleConfirmMerge} disabled={isSubmitDisabled}>
        {loading ? 'Merging...' : 'Confirm & Merge'}
      </CustomButton>
    </div>
  );

  return (
    <Dialog
      header={
        <div className="rpt-dialog-header-title">
          <i className="pi pi-directions text-primary" />
          <span>Merge Companies</span>
        </div>
      }
      visible={show}
      style={{ width: '540px', maxWidth: '95vw' }}
      footer={footerContent}
      onHide={onHide}
      className="rpt-merge-modal">
      <div className="rpt-company-modal-form py-2">
        {/* Selected Companies to Merge Chips */}
        {selectedSources.length > 0 && (
          <div className="rpt-company-modal-form__field">
            <label className="rpt-company-modal-form__label">
              Selected Companies to Merge ({selectedSources.length})
            </label>
            <div className="rpt-company-modal-form__chips-container">
              {selectedSources.map((comp) => {
                const compName = comp.name || comp.CompanyName || comp.companyName;
                const compKey = comp.id || comp.ID || compName;
                return (
                  <span key={compKey} className="rpt-alias-chip rpt-alias-chip--removable">
                    {compName}
                    <span
                      className="rpt-alias-chip__remove-btn"
                      title="Remove company from merge"
                      onClick={() => handleRemoveSourceCompany(compKey)}>
                      &times;
                    </span>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Target Master Company AutoComplete */}
        <div className="rpt-company-modal-form__field mt-2">
          <label className="rpt-company-modal-form__label">
            Target Company <span className="text-red-500">*</span>
          </label>
          <AutoComplete
            value={selectedTargetCompany ? selectedTargetCompany.name : targetSearchQuery}
            suggestions={searchSuggestions}
            completeMethod={handleSearchCompanies}
            field="name"
            placeholder="Search existing company or type target name..."
            onChange={handleInputChange}
            onSelect={handleSelectTarget}
            className="w-full"
            inputClassName={`p-inputtext-sm w-full ${validationError ? 'p-invalid' : ''}`}
            emptyMessage={
              isSearchingTarget ? 'Searching...' : 'Type name to use as new canonical company'
            }
            itemTemplate={(item) => (
              <div className="rpt-autocomplete-item flex align-items-center justify-content-between">
                <div>
                  <span className="rpt-autocomplete-item__primary font-bold">{item.name}</span>
                </div>
              </div>
            )}
          />
          {validationError && (
            <small className="p-error block text-xs mt-1">{validationError}</small>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default MergeCompanyModal;
